// src/pages/AdicionarMotoAgencia.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../firebase";
import {
  setDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  runTransaction,
  updateDoc,
  where,
} from "firebase/firestore";

import { uploadFotoJpg, uploadVideoAny } from "../services/storage";
import { webhookPublishAll } from "../services/webhooks";
import { generateCodigoMoto } from "../utils/codigoMoto";

// Placeholders
import ph1 from "../assets/Foto_01.png";
import ph2 from "../assets/Foto_02.png";
import ph3 from "../assets/Foto_03.png";
import ph4 from "../assets/Foto_04.png";
import ph5 from "../assets/Foto_05.png";
import ph6 from "../assets/Foto_06.png";
import ph7 from "../assets/Foto_07.png";
import phVideo from "../assets/Video_01.png";

const FOTO_PLACEHOLDERS = [ph1, ph2, ph3, ph4, ph5, ph6, ph7];

export default function AdicionarMotoAgencia() {
  const nav = useNavigate();

  const [me, setMe] = useState(null); // users/{uid}
  const [loadingMe, setLoadingMe] = useState(true);

  // Agencia detectada a partir del user
  const [agencyDoc, setAgencyDoc] = useState(null); // {id, agenciaId, nombre, limitePublicaciones, usadas, ...}
  const [loadingAgency, setLoadingAgency] = useState(true);

  // Límite de publicaciones
  const limite = Number(agencyDoc?.limitePublicaciones);
  const usadas = Number(agencyDoc?.usadas) || 0;

  // Form canónico (usa sucursalId/sucursalNombre; para agencia => sucursalId vacío, sucursalNombre=nombre de la agencia)
  const [form, setForm] = useState({
    nombre: "",
    codigoMotoPreview: "",
    marca: "",
    modelo: "",
    ano: "",
    color: "",
    kilometros: "",
    cilindrada: "",
    valor: "",
    valorRebajado: "",
    financiamiento: "No",
    origen: "Nacional",
    tipoMoto: "",
    categoria: "Seminueva",
    sucursalId: "",        // ← para agencia queda vacío
    sucursalNombre: "",    // ← nombre visible (agencia)
    fuente: "Dashboard",
    descripcion: "",
    etiqueta: "",
  });

  // Draft
  const [draftId, setDraftId] = useState(""); // doc id tras guardar datos
  const [savingDraft, setSavingDraft] = useState(false);

  // Media
  const [fotoUrls, setFotoUrls] = useState(Array(7).fill(null));
  const [videoUrl, setVideoUrl] = useState("");
  const [main, setMain] = useState("");

  const fotoInputsRef = useRef(Array.from({ length: 7 }, () => null));
  const videoInputRef = useRef(null);

  const hasAllPhotos = useMemo(() => fotoUrls.every(Boolean), [fotoUrls]);
  const hasVideo = useMemo(() => !!videoUrl, [videoUrl]);
  const readyToPublish = hasAllPhotos && hasVideo;

  const cupoLleno = useMemo(() => {
    if (!Number.isFinite(limite) || limite <= 0) return false; // sin límite
    return usadas >= limite;
  }, [limite, usadas]);

  // Cargar user
  useEffect(() => {
    (async () => {
      const u = auth.currentUser;
      if (!u) { nav("/"); return; }
      const usnap = await getDoc(doc(db, "users", u.uid));
      const data = usnap.exists() ? usnap.data() : null;
      setMe({ uid: u.uid, email: u.email, ...data });
      setLoadingMe(false);
    })();
  }, [nav]);

  // Resolver agencia del user
  useEffect(() => {
    (async () => {
      if (!me || me.rol !== "agencia") { setLoadingAgency(false); return; }
      setLoadingAgency(true);
      try {
        let ref = null;
        let data = null;

        // Preferencia: users/{uid}.agenciaId
        if (me.agenciaId) {
          const qy = query(collection(db, "agencias"), where("agenciaId", "==", me.agenciaId));
          const res = await getDocs(qy);
          if (!res.empty) {
            const d = res.docs[0];
            ref = d.ref;
            data = { id: d.id, ...d.data() };
          }
        }

        // Fallback por email de contacto
        if (!ref && me.email) {
          const qy = query(collection(db, "agencias"), where("contacto.email", "==", me.email));
          const res = await getDocs(qy);
          if (!res.empty) {
            const d = res.docs[0];
            ref = d.ref;
            data = { id: d.id, ...d.data() };
          }
        }

        // Fallback por nombre/sucursal legacy
        if (!ref && me.sucursal) {
          let res = await getDocs(query(collection(db, "agencias"), where("nombre", "==", me.sucursal)));
          if (res.empty) res = await getDocs(query(collection(db, "agencias"), where("agenciaId", "==", me.sucursal)));
          if (!res.empty) {
            const d = res.docs[0];
            ref = d.ref;
            data = { id: d.id, ...d.data() };
          }
        }

        setAgencyDoc(data || null);
        setForm(v => ({
          ...v,
          sucursalId: "",                                // agencias no usan sucursalId
          sucursalNombre: data?.nombre || v.sucursalNombre || "", // visible
        }));
      } catch (e) {
        console.error("[AdicionarMotoAgencia] agencia:", e);
        setAgencyDoc(null);
      } finally {
        setLoadingAgency(false);
      }
    })();
  }, [me]);

  // Preview del código
  useEffect(() => {
    const codigo = generateCodigoMoto({
      marca: form.marca,
      modelo: form.modelo,
      ano: form.ano,
    });
    setForm(v => ({ ...v, codigoMotoPreview: codigo }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.marca, form.modelo, form.ano]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm(v => ({ ...v, [name]: value }));
  };

  // 1) Guardar borrador (crea doc con estatus "pending"). Luego se habilitan subidas.
  const guardarBorrador = async () => {
    if (!me || me.rol !== "agencia") return;

    // Validaciones mínimas
    if (!form.marca || !form.modelo || !form.ano) {
      alert("Completa marca, modelo y año antes de guardar.");
      return;
    }
    if (!agencyDoc?.nombre || !agencyDoc?.agenciaId) {
      alert("No se pudo resolver tu agencia. Contacta al admin.");
      return;
    }
    if (cupoLleno) {
      alert("Has alcanzado el límite de publicaciones activas de tu agencia.");
      return;
    }

    setSavingDraft(true);
    try {
      const codigoMoto = generateCodigoMoto({
        marca: form.marca,
        modelo: form.modelo,
        ano: form.ano,
        date: new Date(),
      });

      const payload = {
        // básicos
        nombre: (form.nombre || "").trim(),
        codigoMoto,
        marca: (form.marca || "").trim(),
        modelo: (form.modelo || "").trim(),
        ano: form.ano ? Number(form.ano) : null,

        color: (form.color || "").trim(),
        kilometros: form.kilometros === "" ? null : Number(form.kilometros),
        cilindrada: form.cilindrada === "" ? null : Number(form.cilindrada),

        valor: form.valor === "" ? null : Number(form.valor),
        valorRebajado: form.valorRebajado === "" ? 0 : Number(form.valorRebajado),

        financiamiento: form.financiamiento === "Sí" ? "Sí" : "No",
        origen: form.origen === "Importada" ? "Importada" : "Nacional",
        tipoMoto: (form.tipoMoto || "").trim(),
        categoria: form.categoria === "Nueva" ? "Nueva" : "Seminueva",

        // asociación (canónicos + legacy mirror)
        sucursalId: "", // agencias no tienen sucursalId
        sucursalNombre: agencyDoc?.nombre || "Agencia",
        sucursal: agencyDoc?.nombre || "Agencia", // legacy (temporal)

        agenciaId: agencyDoc?.agenciaId || null,
        sellerId: null,

        // publicación y control
        estatus: "pending",
        publicadaPor: null, // se fija al publicar
        expiresAt: null,     // agencias no caducan

        // fuente/links
        fuente: "Dashboard",
        descripcion: form.descripcion ?? "",
        etiqueta: form.etiqueta || "",
        link_facebookPage: "",
        link_instagram: "",
        link_mercadoLibre: "",
        link_wooCommerce: "",
        link_youtube: "",

        // media (vacío en draft)
        foto1: null, foto2: null, foto3: null, foto4: null,
        foto5: null, foto6: null, foto7: null, video: null,

        // timestamps
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      await setDoc(doc(db, "motos", codigoMoto), payload, { merge: true });
      setDraftId(codigoMoto);
      alert("Borrador guardado. Ya puedes subir fotos y video.");
    } catch (e) {
      console.error(e);
      alert("No se pudo guardar el borrador.");
    } finally {
      setSavingDraft(false);
    }
  };

  // Subidas (habilitadas sólo si hay draftId)
  const uploadFoto = async (index, file) => {
    if (!draftId) return;
    try {
      const { url } = await uploadFotoJpg(draftId, index + 1, file);
      setFotoUrls(prev => {
        const next = [...prev];
        next[index] = url;
        return next;
      });
      if (!main) setMain(url);

      await updateDoc(doc(db, "motos", draftId), {
        [`foto${index + 1}`]: url,
        updatedAt: new Date().toISOString(),
      });
    } catch (err) {
      console.error(err);
      alert("No se pudo subir la foto.");
    }
  };

  const uploadVideo = async (file) => {
    if (!draftId) return;
    try {
      const url = await uploadVideoAny(draftId, file);
      setVideoUrl(url);
      await updateDoc(doc(db, "motos", draftId), {
        video: url,
        updatedAt: new Date().toISOString(),
      });
    } catch (err) {
      console.error(err);
      alert("No se pudo subir el video.");
    }
  };

  // 2) Publicar ahora
  const publicarAhora = async () => {
    if (!draftId) { alert("Primero guarda el borrador."); return; }
    if (cupoLleno) { alert("Has alcanzado el límite de publicaciones activas de tu agencia."); return; }
    if (!readyToPublish) { alert("Para publicar debes subir 7 fotos y 1 video."); return; }

    try {
      const ref = doc(db, "motos", draftId);
      // Carga doc actual
      const snap = await getDoc(ref);
      if (!snap.exists()) { alert("No se encontró el borrador."); return; }
      const current = snap.data();

      // Armar payload final de publicación (canónicos + legacy mirror asegurados)
      const payloadPublish = {
        estatus: "publicada",
        publicadaPor: "agencia",
        updatedAt: new Date().toISOString(),
        agenciaId: agencyDoc?.agenciaId || current.agenciaId || null,
        sellerId: null,
        expiresAt: null,
        sucursalId: "", // agencias
        sucursalNombre: agencyDoc?.nombre || current.sucursalNombre || current.sucursal || "Agencia",
        sucursal: agencyDoc?.nombre || current.sucursal || "Agencia", // legacy mirror
        publishedOnce: true,
      };

      await updateDoc(ref, payloadPublish);

      const fotosArr = [
        current.foto1, current.foto2, current.foto3, current.foto4,
        current.foto5, current.foto6, current.foto7,
      ].filter(Boolean);

      // Webhook publicar (webhooks.js ya usa sucursalName = moto.sucursalNombre || moto.sucursal)
      await webhookPublishAll(draftId, { ...current, ...payloadPublish }, fotosArr, current.video || "");

      // Incrementar usadas en agencia (transacción)
      if (agencyDoc?.id) {
        const aref = doc(db, "agencias", agencyDoc.id);
        await runTransaction(db, async (tx) => {
          const s = await tx.get(aref);
          if (!s.exists()) return;
          const data = s.data();
          const lim = Number(data.limitePublicaciones);
          const usd = Number(data.usadas) || 0;
          if (Number.isFinite(lim) && lim > 0 && usd >= lim) {
            throw new Error("La agencia alcanzó su límite durante la publicación.");
          }
          tx.update(aref, { usadas: usd + 1, updatedAt: new Date() });
        });
      }

      alert("Moto publicada.");
      nav(`/detalle/${draftId}`);
    } catch (e) {
      console.error(e);
      alert(e.message || "No se pudo publicar.");
    }
  };

  if (loadingMe || loadingAgency) return <div className="p-6 text-gray-600">Cargando…</div>;
  if (me?.rol !== "agencia") return <div className="p-6 text-gray-600">Acceso restringido.</div>;

  return (
    <div className="min-h-screen bg-gray-100 text-gray-900">
      {/* Topbar */}
      <div className="px-6 py-4 border-b border-gray-200 bg-white flex justify-between items-center">
        <button
          onClick={() => nav("/dashboard")}
          className="px-3 py-2 rounded bg-gray-900 text-white hover:bg-gray-800"
        >
          ← Volver
        </button>
        <h1 className="font-semibold truncate">Adicionar Moto (Agencia)</h1>
        <div />
      </div>

      <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Galería */}
        <div>
          {main ? (
            <img
              src={main}
              alt="principal"
              className="w-full h-96 object-cover rounded border border-gray-200 bg-white"
            />
          ) : (
            <div className="w-full h-96 rounded border border-gray-200 bg-gray-50" />
          )}

          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mt-3">
            {Array.from({ length: 7 }).map((_, i) => {
              const show = fotoUrls[i] || FOTO_PLACEHOLDERS[i];
              const disabled = !draftId;
              return (
                <div key={i} className="relative group">
                  <img
                    src={show}
                    alt={`foto-${i + 1}`}
                    className={`w-full aspect-square object-cover rounded border ${
                      main === (fotoUrls[i] || show) ? "border-gray-900" : "border-gray-200"
                    } ${disabled ? "opacity-60" : ""}`}
                    onClick={() => {
                      if (disabled) return;
                      fotoUrls[i] ? setMain(fotoUrls[i]) : fotoInputsRef.current[i]?.click();
                    }}
                  />
                  <div className="absolute inset-0 hidden group-hover:flex items-center justify-center gap-2 bg-black/30 rounded">
                    <button
                      className={`px-2 py-1 text-xs rounded border border-gray-200 shadow-sm ${
                        disabled ? "bg-gray-200 text-gray-400 cursor-not-allowed" : "bg-white text-gray-900"
                      }`}
                      disabled={disabled}
                      onClick={() => !disabled && fotoInputsRef.current[i]?.click()}
                    >
                      Subir
                    </button>
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    ref={(el) => (fotoInputsRef.current[i] = el)}
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (file) await uploadFoto(i, file);
                    }}
                  />
                </div>
              );
            })}
          </div>

          {/* Video */}
          <div className="mt-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold">Video</h3>
              <div className="flex gap-2">
                <button
                  className={`px-3 py-1 text-sm rounded ${
                    !draftId
                      ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                      : "bg-gray-900 text-white hover:bg-gray-800"
                  }`}
                  disabled={!draftId}
                  onClick={() => draftId && videoInputRef.current?.click()}
                >
                  {videoUrl ? "Reemplazar video" : "Subir video"}
                </button>
              </div>
            </div>

            <div className="relative">
              {videoUrl ? (
                <video
                  src={videoUrl}
                  className="w-full h-52 rounded border border-gray-200 bg-white"
                  controls
                />
              ) : (
                <img
                  src={phVideo}
                  alt="video-ph"
                  className={`w-full h-52 object-cover rounded border border-gray-200 ${
                    !draftId ? "opacity-60" : "cursor-pointer"
                  }`}
                  onClick={() => draftId && videoInputRef.current?.click()}
                />
              )}
              <input
                type="file"
                accept="video/*"
                className="hidden"
                ref={videoInputRef}
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (file) await uploadVideo(file);
                }}
              />
            </div>
          </div>
        </div>

        {/* Formulario */}
        <div className="space-y-4">
          {/* Cupo */}
          {Number.isFinite(limite) && limite > 0 && (
            <div
              className={`p-3 rounded border ${
                cupoLleno ? "border-amber-400 bg-amber-50 text-amber-800" : "border-gray-200 bg-white text-gray-700"
              }`}
            >
              <b>Cupo de publicaciones:</b> {usadas} / {limite}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-sm text-gray-600">Nombre</label>
              <input
                name="nombre"
                value={form.nombre}
                onChange={onChange}
                className="mt-1 w-full px-3 py-2 rounded border border-gray-300 bg-gray-50 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900"
              />
            </div>

            <div>
              <label className="text-sm text-gray-600">Código (preview)</label>
              <input
                name="codigoMotoPreview"
                value={form.codigoMotoPreview}
                disabled
                className="mt-1 w-full px-3 py-2 rounded border border-gray-200 bg-gray-100 text-gray-600"
              />
            </div>

            <div>
              <label className="text-sm text-gray-600">Marca</label>
              <input
                name="marca"
                value={form.marca}
                onChange={onChange}
                className="mt-1 w-full px-3 py-2 rounded border border-gray-300 bg-gray-50 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900"
              />
            </div>

            <div>
              <label className="text-sm text-gray-600">Modelo</label>
              <input
                name="modelo"
                value={form.modelo}
                onChange={onChange}
                className="mt-1 w-full px-3 py-2 rounded border border-gray-300 bg-gray-50 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900"
              />
            </div>

            <div>
              <label className="text-sm text-gray-600">Año</label>
              <input
                name="ano"
                type="number"
                value={form.ano}
                onChange={onChange}
                className="mt-1 w-full px-3 py-2 rounded border border-gray-300 bg-gray-50 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900"
              />
            </div>

            <div>
              <label className="text-sm text-gray-600">Color</label>
              <input
                name="color"
                value={form.color}
                onChange={onChange}
                className="mt-1 w-full px-3 py-2 rounded border border-gray-300 bg-gray-50 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900"
              />
            </div>

            <div>
              <label className="text-sm text-gray-600">Kilómetros</label>
              <input
                name="kilometros"
                type="number"
                value={form.kilometros}
                onChange={onChange}
                className="mt-1 w-full px-3 py-2 rounded border border-gray-300 bg-gray-50 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900"
              />
            </div>

            <div>
              <label className="text-sm text-gray-600">Cilindrada (cc)</label>
              <input
                name="cilindrada"
                type="number"
                value={form.cilindrada}
                onChange={onChange}
                className="mt-1 w-full px-3 py-2 rounded border border-gray-300 bg-gray-50 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900"
              />
            </div>

            <div>
              <label className="text-sm text-gray-600">Precio (MXN)</label>
              <input
                name="valor"
                type="number"
                value={form.valor}
                onChange={onChange}
                placeholder="Ej. 125000"
                className="mt-1 w-full px-3 py-2 rounded border border-gray-300 bg-gray-50 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900"
              />
            </div>

            <div>
              <label className="text-sm text-gray-600">Precio Rebajado (MXN)</label>
              <input
                name="valorRebajado"
                type="number"
                value={form.valorRebajado}
                onChange={onChange}
                placeholder="0"
                className="mt-1 w-full px-3 py-2 rounded border border-gray-300 bg-gray-50 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900"
              />
            </div>

            <div>
              <label className="text-sm text-gray-600">Financiamiento</label>
              <select
                name="financiamiento"
                value={form.financiamiento}
                onChange={onChange}
                className="mt-1 w-full px-3 py-2 rounded border border-gray-300 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-900"
              >
                <option value="Sí">Sí</option>
                <option value="No">No</option>
              </select>
            </div>

            <div>
              <label className="text-sm text-gray-600">Origen</label>
              <select
                name="origen"
                value={form.origen}
                onChange={onChange}
                className="mt-1 w-full px-3 py-2 rounded border border-gray-300 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-900"
              >
                <option value="Nacional">Nacional</option>
                <option value="Importada">Importada</option>
              </select>
            </div>

            <div>
              <label className="text-sm text-gray-600">Tipo de moto</label>
              <input
                name="tipoMoto"
                value={form.tipoMoto}
                onChange={onChange}
                className="mt-1 w-full px-3 py-2 rounded border border-gray-300 bg-gray-50 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900"
              />
            </div>

            <div>
              <label className="text-sm text-gray-600">Categoría</label>
              <select
                name="categoria"
                value={form.categoria}
                onChange={onChange}
                className="mt-1 w-full px-3 py-2 rounded border border-gray-300 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-900"
              >
                <option value="Seminueva">Seminueva</option>
                <option value="Nueva">Nueva</option>
              </select>
            </div>

            <div>
              <label className="text-sm text-gray-600">Sucursal / Agencia</label>
              <input
                value={form.sucursalNombre}
                readOnly
                className="mt-1 w-full px-3 py-2 rounded border border-gray-200 bg-gray-100 text-gray-600"
              />
            </div>

            <div>
              <label className="text-sm text-gray-600">Fuente (no editable)</label>
              <input
                value={form.fuente}
                readOnly
                className="mt-1 w-full px-3 py-2 rounded border border-gray-200 bg-gray-100 text-gray-600"
              />
            </div>
          </div>

          <div>
            <label className="text-sm text-gray-600">Descripción</label>
            <textarea
              name="descripcion"
              value={form.descripcion}
              onChange={onChange}
              className="mt-1 w-full h-28 px-3 py-2 rounded border border-gray-300 bg-gray-50 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900"
              placeholder="(una línea, sin saltos ni emojis)"
            />
          </div>

          <div>
            <label className="text-sm text-gray-600">Etiqueta</label>
            <select
              name="etiqueta"
              value={form.etiqueta}
              onChange={onChange}
              className="mt-1 w-full px-3 py-2 rounded border border-gray-300 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-900"
            >
              <option value="">—</option>
              <option value="Nuevo Ingreso">Nuevo Ingreso</option>
              <option value="Apartada">Apartada</option>
              <option value="Promoción">Promoción</option>
              <option value="Vendida">Vendida</option>
            </select>
          </div>

          {/* Acciones */}
          <div className="flex flex-wrap gap-3 items-center">
            <button
              onClick={guardarBorrador}
              disabled={savingDraft || !!draftId || cupoLleno}
              className="px-4 py-2 rounded bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50"
            >
              {savingDraft ? "Guardando…" : (!!draftId ? "Borrador creado" : "Guardar borrador")}
            </button>

            <button
              onClick={publicarAhora}
              disabled={!draftId || !readyToPublish || cupoLleno}
              className="px-4 py-2 rounded bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50"
            >
              Publicar ahora
            </button>

            {!draftId && <span className="text-sm text-gray-600">Primero guarda el borrador para habilitar subidas.</span>}
            {draftId && !readyToPublish && <span className="text-sm text-gray-600">Requiere 7 fotos y 1 video antes de publicar.</span>}
            {cupoLleno && <span className="text-sm text-amber-700">Límite de publicaciones activas alcanzado.</span>}
          </div>
        </div>
      </div>
    </div>
  );
}